import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { IntlConfig, IntlProvider } from 'react-intl';

import { IntlContext, IntlLocale } from './data/context/intl-context';
import locales from './data/translations/locales.json';
import translations from './data/translations/nl.json';
import { useQueryParams } from './data/utils/use-query-params';

const onLoadTranslations = (value: string) =>
  import(`./data/translations/${value}.json`);

type IntlLoaderProps = Omit<IntlConfig, 'locale'> & {
  locale: string | null | undefined;
};

export function IntlLoader({
  locale,
  ...props
}: PropsWithChildren<IntlLoaderProps>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [languages] = useState<IntlLocale[]>(locales);
  const [messages, setMessages] = useState<{ [key: string]: string }>(
    translations
  );

  const { lang } = useQueryParams();

  const language = useMemo(
    () =>
      lang
        ? languages.find(option => option.code === lang)?.code ?? 'nl'
        : 'nl',
    [lang, languages]
  );

  useEffect(() => {
    setLoading(true);
    onLoadTranslations(language)
      .then(({ default: newTranslations }) => {
        setMessages(prevState => ({ ...prevState, ...newTranslations }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [lang, language, languages]);

  const value = useMemo(
    () => ({ loading, languages, language }),
    [loading, languages, language]
  );

  return (
    <IntlProvider locale={language} messages={messages}>
      <IntlContext.Provider value={value}>
        {props.children}
      </IntlContext.Provider>
    </IntlProvider>
  );
}
